import {
  ArrowForward,
  CheckCircleOutline,
  ContentCopy,
  Visibility,
  VisibilityOff,
  WarningAmber,
} from "@mui/icons-material"
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import {
  LockerMigrationState,
  LockerPairPreview,
  LockerPairSide,
} from "./lockerMigrationOverride"

export const MIGRATION_STATE_COLOR: Record<
  LockerMigrationState,
  "success" | "default" | "info"
> = {
  active: "success",
  superseded: "default",
  pendingActivation: "info",
}

const CopyableId: FC<{ id: string; to?: string }> = ({ id, to }) => {
  const { t } = useTranslation()
  const short = id.length > 18 ? `${id.slice(0, 8)}…${id.slice(-6)}` : id
  const text = (
    <Typography
      variant="caption"
      fontFamily="monospace"
      color={to ? "primary.main" : "text.secondary"}
    >
      {short}
    </Typography>
  )
  return (
    <Stack direction="row" spacing={0.25} alignItems="center">
      <Tooltip title={id}>
        {to ? (
          <Link to={to} style={{ textDecoration: "none" }}>
            {text}
          </Link>
        ) : (
          <Box>{text}</Box>
        )}
      </Tooltip>
      <Tooltip title={t("copy")}>
        <IconButton
          size="small"
          sx={{ p: 0.25 }}
          onClick={() => navigator.clipboard?.writeText(id)}
          aria-label={t("copy")}
        >
          <ContentCopy sx={{ fontSize: 11 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

/**
 * `outOfService` names the CAUSE of a hide the migration flags did not create.
 * Without it, an operator who has already flipped the service switch sees this
 * side reported "hidden from users", concludes the migration flags are wrong, and
 * starts editing them — the diagram would be truthful about the outcome and
 * actively misleading about the fix.
 */
const VisibilityTag: FC<{
  visible: boolean
  outOfService?: boolean
  dim?: boolean
}> = ({ visible, outOfService, dim }) => {
  const { t } = useTranslation()
  const Icon = visible ? Visibility : VisibilityOff
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Icon
        sx={{
          fontSize: 14,
          color: dim ? "text.disabled" : visible ? "success.main" : "text.disabled",
        }}
      />
      <Typography
        variant="caption"
        fontWeight={dim ? 400 : 600}
        color={dim ? "text.disabled" : visible ? "success.main" : "text.secondary"}
      >
        {visible
          ? t("lockerMigrationOverride.visibleToUsers")
          : outOfService
            ? t("lockerMigrationOverride.hiddenByOperator")
            : t("lockerMigrationOverride.hiddenFromUsers")}
      </Typography>
    </Stack>
  )
}

/** One half of the pair: identity on top, before → after underneath. */
const SideCard: FC<{ side: LockerPairSide; title: string; to?: string }> = ({
  side,
  title,
  to,
}) => {
  const { t } = useTranslation()
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1.5,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: side.changed ? "primary.main" : "divider",
        bgcolor: side.changed ? "action.hover" : "transparent",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ letterSpacing: 0.6, textTransform: "uppercase", fontSize: "0.65rem" }}
      >
        {title}
      </Typography>
      <Box sx={{ mb: 1 }}>
        <CopyableId id={side.id} to={to} />
      </Box>

      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
        <Chip
          size="small"
          variant="outlined"
          color={side.changed ? "default" : MIGRATION_STATE_COLOR[side.before]}
          label={t(`lockerMigrationOverride.state.${side.before}`)}
          sx={{
            height: 20,
            fontSize: "0.68rem",
            opacity: side.changed ? 0.55 : 1,
            textDecoration: side.changed ? "line-through" : "none",
          }}
        />
        {side.changed && (
          <>
            <ArrowForward sx={{ fontSize: 13, color: "text.secondary" }} />
            <Chip
              size="small"
              color={MIGRATION_STATE_COLOR[side.after]}
              label={t(`lockerMigrationOverride.state.${side.after}`)}
              sx={{ height: 20, fontSize: "0.68rem" }}
            />
          </>
        )}
      </Stack>

      <Box sx={{ mt: 0.75 }}>
        <VisibilityTag
          visible={side.afterVisible}
          outOfService={side.outOfService}
          dim={!side.changed}
        />
      </Box>
    </Box>
  )
}

/**
 * The pair, before → after, with a go/no-go verdict underneath.
 *
 * A locker's user-visibility is decided by two booleans spread across two
 * Things, which is exactly the kind of state a person cannot hold in their head
 * while clicking. Rendering both halves side by side — with the resulting
 * visibility spelled out — is what makes "double-listed" and "orphaned"
 * mistakes visible BEFORE the write instead of after a support call.
 */
export const SaLockerMigrationPairDiagram: FC<{
  preview: LockerPairPreview
  primaryTitle: string
  counterpartTitle: string
}> = ({ preview, primaryTitle, counterpartTitle }) => {
  const { t } = useTranslation()
  const { primary, counterpart, verdict } = preview
  const isProblem = verdict === "bothVisible" || verdict === "bothHidden"
  // A `bothHidden` verdict caused by the service switch is not a broken cutover
  // and must not be diagnosed as one — the fix is the panel above, not these
  // flags. Say so instead of leaving the operator to infer it.
  const operatorHideInPlay = primary.outOfService || !!counterpart?.outOfService

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems="stretch"
        sx={{ mb: 1 }}
      >
        <SideCard side={primary} title={primaryTitle} />
        {counterpart && (
          <>
            <Stack justifyContent="center" alignItems="center" sx={{ px: 0.5 }}>
              <Typography variant="caption" color="text.disabled">
                ↔
              </Typography>
            </Stack>
            <SideCard
              side={counterpart}
              title={counterpartTitle}
              to={`/sa/lockers/${counterpart.id}`}
            />
          </>
        )}
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={{
          p: 1,
          borderRadius: 1,
          border: "1px solid",
          borderColor: isProblem ? "warning.main" : "divider",
          bgcolor: "grey.50",
        }}
      >
        {isProblem ? (
          <WarningAmber sx={{ fontSize: 16, color: "warning.main", mt: "1px" }} />
        ) : (
          <CheckCircleOutline
            sx={{ fontSize: 16, color: "success.main", mt: "1px" }}
          />
        )}
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {t(`lockerMigrationOverride.verdict.${verdict}`, {
              id: primary.afterVisible ? primary.id : counterpart?.id,
            })}
          </Typography>
          {operatorHideInPlay && (
            <Typography variant="caption" color="text.secondary" display="block">
              {t("lockerMigrationOverride.verdictOperatorHide")}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  )
}

export default SaLockerMigrationPairDiagram
